import { useState } from 'react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import { formatHebrewRelative } from '../../lib/relativeTime';
import { BotSubNav } from './BotSubNav';
import { BLOCK_REASON_LABELS_HE, type BlockReason } from './bot.schema';
import { useBotBlockedPhones, useBlockPhone, useUnblockPhone } from './botQueries';
import styles from './bot.module.css';

function normalizePhoneIL(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  if (digits.length < 8 || digits.length > 15) return null;
  if (digits.startsWith('972')) return `+${digits}`;
  if (digits.startsWith('0')) return `+972${digits.slice(1)}`;
  return `+${digits}`;
}

export function BlockedPhonesPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const { data: rows = [], isLoading } = useBotBlockedPhones(orgId);
  const blockPhone = useBlockPhone(orgId);
  const unblockPhone = useUnblockPhone(orgId);

  const [phoneInput, setPhoneInput] = useState('');
  const [reasonInput, setReasonInput] = useState<BlockReason>('manual');
  const [notesInput, setNotesInput] = useState('');

  async function handleBlock() {
    const normalized = normalizePhoneIL(phoneInput);
    if (!normalized) {
      toast.error('מספר טלפון לא תקין');
      return;
    }
    try {
      await blockPhone.mutateAsync(
        notesInput
          ? { phone: normalized, reason: reasonInput, notes: notesInput }
          : { phone: normalized, reason: reasonInput },
      );
      setPhoneInput('');
      setNotesInput('');
      toast.success('המספר נחסם');
    } catch {
      toast.error('לא הצלחנו לחסום · נסה שוב');
    }
  }

  async function handleUnblock(id: string, phone: string) {
    if (!window.confirm(`להסיר חסימה מ-‪${phone}‬?`)) return;
    try {
      await unblockPhone.mutateAsync(id);
      toast.success('החסימה הוסרה');
    } catch {
      toast.error('לא הצלחנו להסיר · נסה שוב');
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>מספרים חסומים</h1>
          <p className={styles.subtitle}>מספרים שביקשו STOP או שחסמת ידנית. הבוט לא יענה להם.</p>
        </div>
      </header>

      <BotSubNav />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>חסום מספר חדש</h2>
        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="block-phone">טלפון</label>
            <input
              id="block-phone"
              type="tel"
              className={styles.input}
              dir="ltr"
              placeholder="054-123-4567"
              value={phoneInput}
              onChange={(e) => { setPhoneInput(e.target.value); }}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="block-reason">סיבה</label>
            <select
              id="block-reason"
              className={styles.select}
              value={reasonInput}
              onChange={(e) => { setReasonInput(e.target.value as BlockReason); }}
            >
              <option value="manual">{BLOCK_REASON_LABELS_HE.manual}</option>
              <option value="spam">{BLOCK_REASON_LABELS_HE.spam}</option>
              <option value="stop_keyword">{BLOCK_REASON_LABELS_HE.stop_keyword}</option>
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="block-notes">הערות (אופציונלי)</label>
            <textarea
              id="block-notes"
              className={styles.textarea}
              value={notesInput}
              onChange={(e) => { setNotesInput(e.target.value); }}
            />
          </div>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={blockPhone.isPending}
            onClick={() => { void handleBlock(); }}
            style={{ alignSelf: 'flex-start' }}
          >
            חסום
          </button>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>רשימת חסומים</h2>
        {isLoading ? (
          <p className={styles.notice}>טוען...</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>אין כרגע מספרים חסומים.</p>
        ) : (
          <div className={styles.list}>
            <div className={styles.tableHead}>
              <span>טלפון</span>
              <span>הערות</span>
              <span>סיבה</span>
              <span>נחסם</span>
              <span></span>
            </div>
            {rows.map((r) => (
              <div key={r.id} className={styles.tableRow}>
                <span className={styles.rowPhone}>
                  <bdi dir="ltr">{r.phone}</bdi>
                </span>
                <span className={styles.rowMeta}>{r.notes ?? '—'}</span>
                <span className={styles.rowMeta}>{r.reason ? BLOCK_REASON_LABELS_HE[r.reason] : '—'}</span>
                <span className={styles.rowMeta}>{formatHebrewRelative(r.blocked_at)}</span>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => { void handleUnblock(r.id, r.phone); }}
                >
                  הסר חסימה
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
